import { motion } from 'framer-motion';
import {
  Activity,
  BookOpen,
  GraduationCap,
  MessageSquare,
  Bell,
  Wand2,
  ArrowRight,
  EyeOff,
  Eye,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type { CourseShape } from '@eduborg/shared';
import { useAuth } from '@/lib/auth-store';
import { getActiveRole } from '@/lib/roles';
import { AppHeader } from '@/components/AppHeader';
import { useCoursesQuery } from '@/hooks/queries/useCourses';
import { useEduBorgVisibility } from '@/components/EduBorg/useEduBorgVisibility';
import { cn } from '@/lib/utils';

/**
 * Phase-1 dashboard: hero card + a "what EduBorg's been doing" panel + the
 * core nav surfaces. Real data wires up in Phase 2 (courses) and Phase 5 (AI).
 *
 * The page is intentionally informative-first so a brand-new user sees value
 * before the first course exists.
 */
export function Dashboard() {
  const { state } = useAuth();
  const coursesQuery = useCoursesQuery(state.status === 'authenticated' ? state.token : '');
  const { visible: companionVisible, toggle: toggleCompanion } = useEduBorgVisibility();

  if (state.status !== 'authenticated') return null;

  const profile = state.profile;
  const role = getActiveRole(profile.roles);
  const firstName = (profile.displayName ?? profile.fullName).split(/\s+/)[0] ?? 'there';
  const courses = coursesQuery.data?.courses ?? [];
  const recentCourses = courses.slice(0, 6);
  const isStudent = profile.accountType === 'student';

  return (
    <div className="min-h-screen bg-gradient-to-b from-[hsl(220,40%,98%)] via-background to-secondary/40">
      <AppHeader />

      <main className="container space-y-8 py-8">
        {/* Hero card */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-primary to-[hsl(220,80%,18%)] px-8 py-10 text-primary-foreground shadow-xl sm:px-12"
        >
          <span
            aria-hidden
            className="absolute -left-20 -top-20 h-64 w-64 rounded-full bg-accent/30 blur-3xl"
          />
          <span
            aria-hidden
            className="absolute -bottom-24 right-1/3 h-72 w-72 rounded-full bg-gold/20 blur-3xl"
          />
          {companionVisible && (
            <motion.img
              src="/brand/eduborg-mascot.png"
              alt=""
              aria-hidden
              className="absolute -bottom-2 -right-4 hidden w-44 sm:block lg:w-56"
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
            />
          )}

          {/*
            Companion visibility toggle. Lives inside the welcome banner per
            Dr. Cato — K-12 keeps the mascot on, HE/adult learners can flip
            it off here without affecting anyone else. (Per-org default +
            per-instructor override land when the org-settings table grows.)
          */}
          <button
            type="button"
            onClick={toggleCompanion}
            aria-pressed={companionVisible}
            className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-primary-foreground/20 bg-primary-foreground/5 px-2.5 py-1 text-[11px] font-medium text-primary-foreground/80 backdrop-blur-sm transition-colors hover:bg-primary-foreground/15"
            title={
              companionVisible ? 'Hide the EduBorg mascot for me' : 'Show the EduBorg mascot for me'
            }
          >
            {companionVisible ? (
              <>
                <Eye className="h-3 w-3" aria-hidden /> Mascot on
              </>
            ) : (
              <>
                <EyeOff className="h-3 w-3" aria-hidden /> Mascot off
              </>
            )}
          </button>

          <div className="relative max-w-2xl space-y-3">
            {/* Badge precedence: an org-wide role (instructor / admin / etc.) wins.
                For students, role is null because student-ness is per-course, not
                per-org — fall back to the account_type so the badge says "Student"
                instead of the previously-shown "New here", which read as
                accidentally rude even after they'd joined a class. */}
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-foreground/10 px-3 py-1 text-xs font-medium uppercase tracking-wider">
              {role
                ? role.replace(/_/g, ' ')
                : isStudent
                  ? 'Student'
                  : recentCourses.length === 0
                    ? 'New here'
                    : 'Welcome'}
            </span>
            <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Welcome back, {firstName}.
            </h1>
            <p className="text-primary-foreground/80">
              EduBorg is watching the signal so you don&apos;t have to. Here&apos;s what&apos;s
              moved since you last looked in.
            </p>
            <div className="flex flex-wrap gap-2 pt-3">
              {/* Primary CTA differs by role: instructors land in their build
                  surface (Workspace); students go to the join-a-class flow.
                  "Browse templates" is instructor-only because the templates
                  page itself is Phase-5 and not registered in the SPA yet. */}
              {isStudent ? (
                <Link
                  to="/join"
                  className="inline-flex items-center gap-1.5 rounded-xl bg-accent px-4 py-2.5 text-sm font-semibold text-accent-foreground shadow transition-shadow hover:shadow-lg"
                >
                  Join a class <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
              ) : (
                <>
                  <Link
                    to="/workspace"
                    className="inline-flex items-center gap-1.5 rounded-xl bg-accent px-4 py-2.5 text-sm font-semibold text-accent-foreground shadow transition-shadow hover:shadow-lg"
                  >
                    Open workspace <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                  <Link
                    to="/templates"
                    className="inline-flex items-center gap-1.5 rounded-xl border border-primary-foreground/20 px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-primary-foreground/10"
                  >
                    Browse templates
                  </Link>
                </>
              )}
            </div>
          </div>
        </motion.section>

        {/* What EduBorg has been doing */}
        <section
          aria-labelledby="pulse-h"
          className="rounded-2xl border border-border bg-card p-6 shadow-sm"
        >
          <header className="mb-4 flex items-center justify-between">
            <h2 id="pulse-h" className="font-display text-lg font-semibold">
              EduBorg pulse
            </h2>
            <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
              </span>
              Live
            </span>
          </header>
          {/* Pulse rows are role-aware. Students see what's relevant to them
              (their classes, assignments, threads they joined). Instructors
              see the watching/risk/drafts trio that maps to the work
              EduBorg is doing on their behalf. */}
          <ul className="space-y-3">
            {isStudent ? (
              <>
                <PulseRow
                  icon={Activity}
                  label={
                    courses.length === 0
                      ? 'No classes yet — join one with a code from your teacher.'
                      : `${courses.length} class${courses.length === 1 ? '' : 'es'} on your roster`
                  }
                />
                <PulseRow icon={Bell} label="0 assignments due this week" />
                <PulseRow icon={MessageSquare} label="0 new replies on threads you're in" />
              </>
            ) : (
              <>
                <PulseRow
                  icon={Activity}
                  label={
                    courses.length === 0
                      ? 'Watching: 0 student sessions (no courses yet)'
                      : `Watching ${courses.length} course${courses.length === 1 ? '' : 's'} for activity`
                  }
                />
                <PulseRow icon={Bell} label="0 risk flags this week" />
                <PulseRow icon={MessageSquare} label="0 reply drafts queued" />
              </>
            )}
          </ul>
        </section>

        {/* Your courses */}
        {recentCourses.length > 0 && (
          <section aria-labelledby="courses-h">
            <header className="mb-3 flex items-center justify-between">
              <h2 id="courses-h" className="font-display text-lg font-semibold">
                {isStudent ? 'Your classes' : 'Your courses'}
              </h2>
              <Link
                to="/workspace"
                className="text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                View all →
              </Link>
            </header>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {recentCourses.map((c) => (
                <CourseTile key={c.id} course={c} />
              ))}
            </div>
          </section>
        )}

        {/* Quick actions. */}
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {isStudent ? (
            <ActionCard
              icon={BookOpen}
              title="Join a class"
              body="Type the 6-character code your teacher gave you."
              cta="Join"
              to="/join"
            />
          ) : (
            <ActionCard
              icon={Wand2}
              title="Create a course"
              body="Two-phase course builder — structure first, then full content."
              cta="Start"
              to="/workspace"
            />
          )}
          <ActionCard
            icon={BookOpen}
            title="Open the library"
            body="Browse and clone published course templates from the EduBorg community."
            cta="Browse"
            to="/templates"
          />
          <ActionCard
            icon={GraduationCap}
            title="Tutoring Center"
            body="One-on-one with EduBorg. Step-by-step explanations, no graded-assignment giveaways."
            cta="Ask EduBorg"
            to="/tutor"
          />
        </section>
      </main>
    </div>
  );
}

function CourseTile({ course }: { course: CourseShape }) {
  return (
    <Link
      to={`/c/${course.orgSlug}/${course.slug}`}
      className="group flex items-center gap-3 rounded-2xl border border-border bg-card p-3.5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
        <BookOpen className="h-4 w-4" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-display text-sm font-semibold">{course.title}</span>
        <span className="text-xs text-muted-foreground">
          <span
            className={cn(
              'mr-1.5 inline-block rounded px-1.5 py-0.5 font-medium uppercase tracking-wider',
              course.status === 'active'
                ? 'bg-accent/10 text-accent'
                : 'bg-secondary text-foreground',
            )}
          >
            {course.status}
          </span>
          {course.orgSlug}
        </span>
      </span>
    </Link>
  );
}

function PulseRow({
  icon: Icon,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <li className="flex items-center gap-3">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10 text-accent">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <span className="text-sm text-muted-foreground">{label}</span>
    </li>
  );
}

function ActionCard({
  icon: Icon,
  title,
  body,
  cta,
  to,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
  cta: string;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-xl"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-accent/10 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100"
      />
      <span className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent">
        <Icon className="h-5 w-5" aria-hidden />
      </span>
      <h3 className="relative mt-4 font-display text-base font-semibold">{title}</h3>
      <p className="relative mt-1 text-sm text-muted-foreground">{body}</p>
      <span className="relative mt-3 inline-flex items-center gap-1 text-sm font-semibold text-accent">
        {cta} <ArrowRight className="h-4 w-4" aria-hidden />
      </span>
    </Link>
  );
}
